// Shared profile definition — imported by the UI and by /api/me so the
// completion score is computed the same way on both sides.
//
// Keys are the hub `profiles` column names. Required fields carry 70% of the
// circle, optional fields 30%.

export const ROLES = [
  'Producer', 'CSR', 'Principal', 'Underwriter', 'Marketing Representative', 'Executive', 'Regulator', 'Other',
];

export const OPTIONS = {
  years_licensed: ['Not licensed', '0–2', '3–5', '6–10', '11–20', '20+'],
  designations: ['CPCU', 'CIC', 'CRM', 'CISR', 'CISR Elite', 'ARM', 'AAI', 'AU', 'CLCS', 'CPIA', 'CWCA', 'CRIS', 'RPLU', 'ACSR', 'Other'],
  agency_type: ['Independent agency', 'Captive agency', 'MGA / MGU', 'Wholesale / E&S', 'Carrier', 'Broker', 'Other'],
  agency_size: ['Just me', '2–5', '6–15', '16–50', '51–200', '200+'],
  lines_of_business: ['Commercial P&C', 'Personal lines', 'Workers comp', 'Benefits / Life & Health', 'Surety', 'Specialty / E&S', 'Farm / Ag'],
  ams: ['Applied Epic', 'AMS360', 'HawkSoft', 'EZLynx', 'NowCerts', 'QQCatalyst', 'Sagitta', 'Newton', 'AgencyZoom', 'None', 'Other'],
  crm: ['AgencyZoom', 'Salesforce', 'HubSpot', 'Copper', 'Zywave', 'Built into AMS', 'None', 'Other'],
  phone_system: ['RingCentral', 'Nextiva', '8x8', 'Microsoft Teams', 'Zoom Phone', 'Dialpad', 'Vonage', 'GoTo Connect', 'Other'],
  ai_tools: ['ChatGPT', 'Claude', 'Microsoft Copilot', 'Gemini', 'Perplexity', 'Grammarly', 'Built into AMS', 'None yet', 'Other'],
};

export const REQUIRED_FIELDS = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'email', label: 'Work email' },
  { key: 'alternate_email', label: 'Alternate email' },
  { key: 'work_phone', label: 'Work phone' },
  { key: 'mobile_phone', label: 'Mobile phone' },
  { key: 'company', label: 'Agency name' },
  { key: 'agency_website', label: 'Agency website' },
  { key: 'role', label: 'Role' },
  { key: 'linkedin_url', label: 'LinkedIn profile' },
  { key: 'avatar_url', label: 'Profile photo' },
];

export const OPTIONAL_FIELDS = [
  { key: 'years_licensed', label: 'Years licensed', type: 'select' },
  { key: 'designations', label: 'Designations', type: 'multi' },
  { key: 'agency_type', label: 'Agency type', type: 'select' },
  { key: 'agency_size', label: 'Agency size', type: 'select' },
  { key: 'lines_of_business', label: 'Lines of business', type: 'multi' },
  { key: 'states_licensed', label: 'States licensed', type: 'text', placeholder: 'MI, OH, IN' },
  { key: 'ams', label: 'Agency management system', type: 'select' },
  { key: 'crm', label: 'Agency CRM', type: 'select' },
  { key: 'phone_system', label: 'Agency phone system', type: 'select' },
  { key: 'ai_tools', label: 'AI tools you use regularly', type: 'multi' },
  { key: 'npn', label: 'NPN (National Producer Number)', type: 'text', placeholder: '8–10 digits' },
];

export const EDITABLE_KEYS = [
  ...REQUIRED_FIELDS.map((f) => f.key).filter((k) => k !== 'email' && k !== 'avatar_url'),
  'role_other', 'agency_state',
  ...OPTIONAL_FIELDS.map((f) => f.key),
];
export const ARRAY_KEYS = ['designations', 'lines_of_business', 'states_licensed', 'ai_tools'];

const filled = (v) => (Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined && String(v).trim() !== '');

export function profileCompletion(p = {}) {
  const req = REQUIRED_FIELDS.filter((f) => filled(p[f.key])).length;
  const opt = OPTIONAL_FIELDS.filter((f) => filled(p[f.key])).length;
  const score = (req / REQUIRED_FIELDS.length) * 70 + (opt / OPTIONAL_FIELDS.length) * 30;
  return {
    percent: Math.round(score),
    requiredDone: req === REQUIRED_FIELDS.length,
    missingRequired: REQUIRED_FIELDS.filter((f) => !filled(p[f.key])).map((f) => f.label),
  };
}

// Light validation shared by client and server. Returns { field: message }.
export function validateProfile(p = {}) {
  const errs = {};
  const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (filled(p.alternate_email) && !email.test(p.alternate_email)) errs.alternate_email = 'Enter a valid email.';
  for (const k of ['work_phone', 'mobile_phone']) {
    if (filled(p[k]) && String(p[k]).replace(/\D/g, '').length < 10) errs[k] = 'Enter a 10-digit phone number.';
  }
  if (filled(p.linkedin_url) && !/linkedin\.com\//i.test(p.linkedin_url)) errs.linkedin_url = 'Use your linkedin.com profile link.';
  if (filled(p.npn) && !/^\d{4,10}$/.test(String(p.npn).trim())) errs.npn = 'NPN is digits only.';
  if (p.role === 'Other' && !filled(p.role_other)) errs.role_other = 'Tell us your role.';
  return errs;
}

// US phone -> E.164 (+1XXXXXXXXXX); anything else left as typed.
export function normalizePhone(v) {
  const d = String(v || '').replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return v ? String(v).trim() : null;
}

export function formatPhone(v) {
  const d = String(v || '').replace(/\D/g, '');
  const n = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return n.length === 10 ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : v || '';
}

export function normalizeUrl(v) {
  const s = String(v || '').trim();
  if (!s) return null;
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}
